import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useActions } from "../context/ActionsContext";
import { PageHeader, TableWrap, Th, Td, Loading, EmptyState, Badge } from "../components/shared";
import { formatINR } from "../lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";

export default function Advances() {
  const [cand, setCand] = useState([]);
  const [payee, setPayee] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { open } = useActions();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([api.get("/advances/candidates"), api.get("/advances/payees")]);
      setCand(c.data.rows); setPayee(p.data.rows);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("prabhat:refresh", h);
    return () => window.removeEventListener("prabhat:refresh", h);
  }, [load]);

  const ageBadge = (d) => {
    if (d == null) return null;
    const cls = d > 60 ? "bg-rose-50 text-rose-700 border-rose-200" : d > 30 ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-slate-100 text-slate-600 border-slate-200";
    return <Badge cls={cls}>{d} days</Badge>;
  };

  if (loading) return <Loading />;

  return (
    <div>
      <PageHeader title="Advance Report" subtitle="Candidate advances held & agency advances pending" />
      <Tabs defaultValue="candidate">
        <TabsList>
          <TabsTrigger value="candidate" data-testid="tab-candidate-advances">Candidate Advances ({cand.length})</TabsTrigger>
          <TabsTrigger value="payee" data-testid="tab-payee-advances">Agency / Payee Advances ({payee.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="candidate" className="mt-4">
          {cand.length === 0 ? <EmptyState title="No candidate advances" /> : (
            <TableWrap>
              <thead><tr><Th>Candidate</Th><Th className="text-right">Advance Received</Th><Th className="text-right">Income Adjusted</Th><Th className="text-right">Refunded</Th><Th className="text-right">Pending</Th><Th>Age</Th></tr></thead>
              <tbody>{cand.map((r) => (
                <tr key={r.candidate.id} onClick={() => navigate(`/candidates/${r.candidate.id}`)} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 cursor-pointer">
                  <Td className="font-medium">{r.candidate.name}<div className="text-[10px] text-slate-400 font-mono">{r.candidate.candidate_code}</div></Td>
                  <Td className="text-right font-mono">{formatINR(r.advance_received)}</Td>
                  <Td className="text-right font-mono text-blue-600">{formatINR(r.income_adjusted)}</Td>
                  <Td className="text-right font-mono text-rose-600">{formatINR(r.refunded)}</Td>
                  <Td className="text-right font-mono font-semibold text-violet-600">{formatINR(r.pending)}</Td>
                  <Td>{ageBadge(r.age_days)}</Td>
                </tr>
              ))}</tbody>
            </TableWrap>
          )}
        </TabsContent>
        <TabsContent value="payee" className="mt-4">
          {payee.length === 0 ? <EmptyState title="No agency advances" /> : (
            <TableWrap>
              <thead><tr><Th>Agency / Payee</Th><Th className="text-right">Advance Paid</Th><Th className="text-right">Adjusted</Th><Th className="text-right">Pending</Th><Th>Status</Th><Th>Age</Th></tr></thead>
              <tbody>{payee.map((r) => (
                <tr key={r.payee.id} onClick={() => navigate(`/payees/${r.payee.id}`)} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 cursor-pointer">
                  <Td className="font-medium">{r.payee.name}<div className="text-[10px] text-slate-400">{r.payee.type}</div></Td>
                  <Td className="text-right font-mono">{formatINR(r.advance_paid)}</Td>
                  <Td className="text-right font-mono text-teal-600">{formatINR(r.adjusted)}</Td>
                  <Td className="text-right font-mono font-semibold text-violet-600">{formatINR(r.pending)}</Td>
                  <Td><Badge cls="bg-amber-50 text-amber-700 border-amber-200">{r.status}</Badge></Td>
                  <Td>{ageBadge(r.age_days)}</Td>
                </tr>
              ))}</tbody>
            </TableWrap>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
